const API_URL =
  (typeof import.meta !== 'undefined' &&
    (import.meta as any).env?.VITE_API_URL) ||
  '';

export interface ReconciliationGroupBreakdown {
  group_name: string;
  group_id?: string | null;
  total_conversions: number;
  confirmed_commission: number;
  cancelled_count: number;
  cancelled_amount: number;
  net_profit: number;
}

export interface ReconciliationCampaignBreakdown {
  campaign_name: string;
  queue_id?: string | null;
  total_conversions: number;
  confirmed_commission: number;
  cancelled_count: number;
  cancelled_amount: number;
  net_profit: number;
}

export interface ReconciliationSummary {
  total_rows: number;
  matched_conversions: number;
  unmatched_conversions: number;
  match_rate_percent: number;
  total_commission: number;
  total_cancelled: number;
  ad_spend: number;
  net_profit: number;
  by_group: ReconciliationGroupBreakdown[];
  by_campaign: ReconciliationCampaignBreakdown[];
}

export interface AffiliateConversionResponse {
  id: string;
  reconciliation_id: string;
  marketplace: string;
  subid?: string | null;
  project?: string | null;
  category?: string | null;
  group_token?: string | null;
  order_id?: string | null;
  item_title?: string | null;
  item_sku?: string | null;
  status: 'confirmed' | 'cancelled' | 'returned' | 'pending' | string;
  quantity: number;
  sale_amount: number;
  commission_amount: number;
  matched_offer_id?: string | null;
  matched_offer_title?: string | null;
  matched_group_id?: string | null;
  matched_group_name?: string | null;
  matched_queue_id?: string | null;
  matched_queue_name?: string | null;
  conversion_date?: string | null;
  created_at: string;
}

export interface FinancialReconciliationResponse {
  id: string;
  tenant_id: string;
  filename: string;
  marketplace: string;
  total_rows: number;
  matched_conversions: number;
  unmatched_conversions: number;
  total_commission: number;
  total_cancelled: number;
  ad_spend: number;
  net_profit: number;
  status: string;
  error_message?: string | null;
  summary: ReconciliationSummary;
  conversions: AffiliateConversionResponse[];
  created_at: string;
  updated_at: string;
}

export interface FinancialReconciliationListItem {
  id: string;
  tenant_id: string;
  filename: string;
  marketplace: string;
  total_rows: number;
  matched_conversions: number;
  unmatched_conversions: number;
  total_commission: number;
  total_cancelled: number;
  ad_spend: number;
  net_profit: number;
  status: string;
  created_at: string;
}

export async function uploadReconciliationCsv(
  token: string,
  file: File,
  marketplace?: string,
  adSpend: number = 0
): Promise<FinancialReconciliationResponse> {
  const formData = new FormData();
  formData.append('file', file);
  if (marketplace && marketplace !== 'auto') {
    formData.append('marketplace', marketplace);
  }
  formData.append('ad_spend', String(adSpend));

  const response = await fetch(`${API_URL}/api/v1/reconciliation/upload-csv`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.detail || 'Falha ao processar conciliação do arquivo CSV.'
    );
  }

  return response.json();
}

export async function fetchReconciliationHistory(
  token: string
): Promise<FinancialReconciliationListItem[]> {
  const response = await fetch(
    `${API_URL}/api/v1/reconciliation/history?limit=20`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Falha ao carregar histórico de conciliações.');
  }

  return response.json();
}

export async function fetchReconciliationDetails(
  token: string,
  id: string
): Promise<FinancialReconciliationResponse> {
  const response = await fetch(`${API_URL}/api/v1/reconciliation/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Falha ao obter detalhes da conciliação.');
  }

  return response.json();
}

export async function deleteReconciliation(
  token: string,
  id: string
): Promise<void> {
  const response = await fetch(`${API_URL}/api/v1/reconciliation/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Falha ao excluir registro de conciliação.');
  }
}
